import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**Services**/
import { attachIoTPolicyToCognitoIdentity } from "../services/AuthService";
import { setUserDataForAPI } from "../services/AxiosInterceptor";
import { getInitialCombinedDetails } from "../services/UserService";

/**Components**/
import LoadingSpinner from "../utils/components/common/loadingSpinner/LoadingSpinner";
import PagesHeader from "./Header";
import Sidebar from "./Sidebar";

/**Other Config/Utils/Constants**/
import {
  USER_ROLES,
  logoutUserFromCurrentDevice,
  removeAllLocalValuesOfUser,
  updateCommonInfo,
} from "../../src/utils/constants/userConstants";
import { getUserRole, useEffectOnce } from "../../src/utils/utils";
import { backendConfig } from "../config/BackendConfig";
import AppRoutes from "./Routes";

/**Third-Part Components**/
import createActivityDetector from "activity-detector";
import { Auth, PubSub } from "aws-amplify";
import { browserName, isMobile, osName } from "react-device-detect";
import { useNavigate } from "react-router-dom";

import "./MainLayout.scss";
import { getDeviceSize } from "../utils/constants/deviceConstants";

/**Global Context Starts**/
export const UserContext = createContext();
export const NotificationContext = createContext({});
export const SortingContext = createContext({});
export const OrganisationContext = createContext({
  orgData: null,
  siteData: null,
  setSiteData: () => {},
});
export const ReportsDownloadUpdate = createContext({
  reportUpdateStatus: [],
  setReportUpdateStatus: () => {},
});
export const LocalValuesContext = createContext({});
/**Global Context Ends**/

export function MainLayout() {
  const navigate = useNavigate();
  const [userDetail, setUserDetail] = useState({});
  const [userRole, setUserRole] = useState(USER_ROLES.superAdminCorp);
  const [orgDetail, setOrgDetail] = useState({});
  const [userCognitoDetail, setUserCognitoDetail] = useState({});
  const [siteDetail, setSiteDetail] = useState({});
  const [reportDownloadDetail, setReportDownloadDetail] = useState([]);
  const [isNewMode, setIsNewMode] = useState(false);
  const [isEvtListedFromDevice, setIsEvtListedFromDevice] = useState(false);
  const [isEvtListedFromHeader, setIsEvtListedFromHeader] = useState(false);
  const [isNewNotification, setIsNewNotification] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [globalDeviceDetails, setGlobalDeviceDetails] = useState(null);
  const [globalLocationDetails, setGlobalLocationDetails] = useState(null);
  const [loadMainData, setLoadMainData] = useState(false);
  const [appTheme, setAppTheme] = useState("light");

  const [activeFilter, setActiveFilter] = useState("All");
  const [isShowUnread, setIsShowUnread] = useState(true);
  const [isShowEvent, setIsShowEvent] = useState(false);

  const eventsSub = useRef(null);
  const reportSub = useRef(null);
  const graphSub = useRef(null);
  const summarySub = useRef(null);
  const [graphReportStatus, setGraphReportStatus] = useState(null);
  const [summaryReportStatus, setSummaryReportStatus] = useState(null);
  const [deviceQueryCount, setDeviceQueryCount] = useState(0);
  const [isDashboardSorting, setIsDashboardSorting] = useState(false);

  const [refreshRateDeviceView, setRefreshRateDeviceView] = useState(false);
  const [refreshRateDashboard, setRefreshRateDashboard] = useState(false);

  const defaultSelectedSortKey = localStorage.getItem("selectedSortKey");

  /**MQTT Event Subscriptions Starts**/
  const graphSubscription = useCallback(async (userDetails) => {
    if (graphSub.current) {
      await graphSub.current.unsubscribe();
    }
    graphSub.current = PubSub.subscribe(
      "chorus/" +
        backendConfig.PUBSUB_ENV +
        "/user/" +
        userDetails?.PK?.slice(2) +
        "/reports/graph/events"
    ).subscribe({
      next: (data) => {
        setGraphReportStatus(data?.value);
      },
      error: (error) => {},
      close: () => {},
    });
  }, []);

  const summarySubscription = useCallback(async (userDetails) => {
    if (summarySub.current) {
      await graphSub.current.unsubscribe();
    }
    summarySub.current = PubSub.subscribe(
      "chorus/" +
        backendConfig.PUBSUB_ENV +
        "/user/" +
        userDetails?.PK?.slice(2) +
        "/reports/summary/events"
    ).subscribe({
      next: (data) => {
        setSummaryReportStatus(data?.value);
      },
      error: (error) => {},
      close: () => {},
    });
  }, []);

  /**Callback of addReportsSub**/
  const handleReportsSub = useCallback((data) => {
    let response = data?.value;
    if (
      data?.value?.reportStatus === "Completed" ||
      data?.value?.reportStatus === "Failed"
    ) {
      setReportDownloadDetail((prevVal) => {
        return [...prevVal, response];
      });
    }
  }, []);

  const addReportsSub = useCallback(
    async (userDetails) => {
      if (reportSub.current) {
        await reportSub.current.unsubscribe();
      }
      reportSub.current = PubSub.subscribe(
        "chorus/" +
          backendConfig.PUBSUB_ENV +
          "/user/" +
          userDetails?.PK?.slice(2) +
          "/reports/events"
      ).subscribe({
        next: (data) => handleReportsSub(data),
        error: (error) => {},
        close: () => {},
      });
    },
    [handleReportsSub]
  );

  /**Callback of eventSubscribe**/
  const logoutUser = useCallback(async (userName, deviceKey) => {
    try {
      await logoutUserFromCurrentDevice(userName, deviceKey);
    } catch (err) {}
    setIsLoading(false);
  }, []);

  const eventSubscribe = useCallback(
    async (userDetails, userCognitoData) => {
      if (eventsSub.current) {
        await eventsSub.current.unsubscribe();
      }

      eventsSub.current = PubSub.subscribe(
        "chorus/" +
          backendConfig.PUBSUB_ENV +
          "/users/" +
          userDetails?.PK?.slice(2) +
          "/notifications/events"
      ).subscribe({
        next: (data) => {
          setIsNewNotification((prevVal) => !prevVal);
          if (data.value.EventName === "Mode Changed") {
            setIsNewMode(true);
          }

          const deviceKey =
            userCognitoData?.signInUserSession?.accessToken?.payload
              ?.device_key;
          if (
            data.value.eventType === "logOut" &&
            data.value.deviceId === deviceKey
          ) {
            setIsLoading(true);
            logoutUser(userCognitoData?.username, deviceKey);
          }

          if (
            data.value.EventType === "deviceEvents" &&
            (defaultSelectedSortKey === "deviceNameSort" ||
              defaultSelectedSortKey === "eventAndFaultSort")
          ) {
            if (data.value.EventName === "Name Changed") {
              setIsDashboardSorting((prev) => !prev);
            }
          }

          if (
            (data.value.EventType === "cartridgeLevel" &&
              defaultSelectedSortKey === "cartridgeLevelSort") ||
            (data.value.EventType === "clo2Value" &&
              defaultSelectedSortKey === "clo2ValueSort") ||
            (data.value.EventType === "faults" &&
              defaultSelectedSortKey === "faultSort") ||
            (data.value.EventType === "connectivity" &&
              defaultSelectedSortKey === "eventAndFaultSort")
          ) {
            setIsDashboardSorting(!isDashboardSorting);
          }
        },
        error: () => {},
        close: () => {},
      });
    },
    [defaultSelectedSortKey, logoutUser]
  );
  /**MQTT Event Subscriptions Ends**/

  /**Remove localStorage and session values before unload STARTS**/
  useEffect(() => {
    const handleBeforeUnload = () => {
      removeAllLocalValuesOfUser();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);
  /**Remove localStorage and session values before unload ENDS**/

  /**Logout user on Idle state**/
  const onIdle = useCallback(async () => {
    sessionStorage.removeItem("keepmelogin");
    localStorage.setItem("keepmelogin", false);
    await Auth.signOut();
    window.location.reload(true);
  }, []);

  /**Function to detect idle state of Web APP **/
  const setIdelTimer = useCallback(
    (keepMeLoginDays) => {
      let idle_days = 1; // Default idle Day is 1 if keepMeLoginDays is not there.

      if (keepMeLoginDays) {
        idle_days = Number(keepMeLoginDays);
        if (idle_days > 24) {
          idle_days = 24;
        }
      }

      let timer = 86400000; // 24 hours
      const keepmelogin = localStorage.getItem("keepmelogin");
      if (!(keepmelogin == null || keepmelogin === "false")) {
        timer = idle_days * 86400000;
      }

      const activityDetector = createActivityDetector({
        timeToIdle: timer, // wait 20s of inactivity to consider the user is idle
        ignoredEventsWhenIdle: [],
        inactivityEvents: [],
      });
      activityDetector.on("idle", onIdle);
    },
    [onIdle]
  );

  const attachIoTPolicy = useCallback(async (username) => {
    try {
      const info = await Auth.currentCredentials();

      const cognitoIdentityId = info.identityId;

      const queryParam = {
        cognitoIdentityId: cognitoIdentityId,
        cognitoId: username,
      };

      await attachIoTPolicyToCognitoIdentity(queryParam);
    } catch (error) {
      console.log(error);
    }
  }, []);

  const updateLogin = async (
    deviceKey,
    username,
    lastLoginTime,
    KeepMeLoggedInDays = 1
  ) => {
    try {
      if (lastLoginTime) {
        let date = new Date(lastLoginTime);
        let currentTime = new Date();

        const diffTime = Math.abs(currentTime - date);
        const diffDays = diffTime / (1000 * 60 * 60 * 24);

        if (Number(diffDays) > Number(KeepMeLoggedInDays)) {
          await logoutUser(username, deviceKey);
          setIsLoading(false);
          return;
        }
      }
      setIsLoading(false);

      let current_date = new Date().toJSON();
      const value = {
        lastLoginTime: current_date,
        deviceLogin: true,
        deviceDetails: {
          deviceType: "Web",
          osType: osName,
          deviceModel: `${browserName}${isMobile ? " Mobile" : ""} Browser`,
        },
      };
      updateCommonInfo(username, [deviceKey], value);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  const getDetailsForCurrentUser = useCallback(
    (username, devicekey, congnitoData) => {
      const deviceCount = getDeviceSize();
      setDeviceQueryCount(deviceCount);
      setDeviceQueryCount(deviceCount);

      const queryParam = {
        cognitoId: username,
        deviceId: devicekey,
        deviceType: 0,
        size: deviceCount,
        from: 0,
      };

      setIsLoading(true);
      getInitialCombinedDetails(queryParam)
        .then((response) => {
          const userDetails = response?.data?.UserDetails;

          const orgData = response?.data.TenantDetails;
          orgData.SiteList = response?.data.SiteDetails;

          /**Starts: Logic to logout user if  forceLogout==true and stop further execution**/
          let isLogoutFromDeviceActive = false;
          const commonInfo = userDetails?.ClientData?.commonInfo;
          let lastLoginTime;
          if (commonInfo) {
            const commonInfoKey = `${devicekey}&${username}`;
            const payloadSortingKey =
              commonInfo[commonInfoKey]?.selectedSortOption;

            if (payloadSortingKey) {
              localStorage.setItem("selectedSortKey", payloadSortingKey);
            }

            if (
              commonInfo[commonInfoKey] &&
              commonInfo[commonInfoKey]?.forceLogout
            ) {
              isLogoutFromDeviceActive = true;
              logoutUserFromCurrentDevice(congnitoData?.username, devicekey);
            }
            lastLoginTime = commonInfo[commonInfoKey]?.lastLoginTime;
          }
          if (isLogoutFromDeviceActive) return;
          /**Ends: Logic to logout user if  forceLogout==true and stop further execution**/

          let fetchedRefreshRateDeviceView =
            userDetails?.ClientData?.webInfo?.refreshRateDeviceView || 300000;

          let fetchedRefreshRateDashboard =
            userDetails?.ClientData?.webInfo?.refreshRateDashboard || 300000;

          setRefreshRateDeviceView(fetchedRefreshRateDeviceView);
          setRefreshRateDashboard(fetchedRefreshRateDashboard);

          localStorage.setItem(
            "refreshRateDashboard",
            fetchedRefreshRateDashboard
          );
          localStorage.setItem(
            "refreshRateDeviceView",
            fetchedRefreshRateDeviceView
          );

          let themeValue =
            userDetails?.ClientData?.webInfo?.selectedTheme || "light";
          localStorage.setItem("selectedTheme", themeValue);
          if (themeValue === "system") {
            const isDarkMode =
              window.matchMedia &&
              window.matchMedia("(prefers-color-scheme: dark)").matches;
            themeValue = isDarkMode ? "dark" : "light";
          }
          setAppTheme(themeValue);

          /**Device Details for Dashboard starts**/
          setLoadMainData(true);
          setGlobalDeviceDetails(response?.data?.DeviceDetails);
          setGlobalLocationDetails(response?.data?.LocationDetails);

          const filters = userDetails?.ClientData?.webInfo?.customFilters;
          const selectedSiteId =
            userDetails?.ClientData?.webInfo?.selectedSiteId;
          if (filters?.length) {
            localStorage.setItem("filters", JSON.stringify(filters));
          }
          if (selectedSiteId) {
            localStorage.setItem("currentSite", selectedSiteId);
          }
          /**Device Details for Dashboard ends**/

          if (!userDetails?.isPolicyAlreadyAttachedToId) {
            attachIoTPolicy(username); // call for null & false
          }

          /**Starts: Set all Subscriptions**/
          addReportsSub(userDetails);
          graphSubscription(userDetails);
          summarySubscription(userDetails);
          eventSubscribe(userDetails, congnitoData);
          /**Ends: Set all Subscriptions**/

          const user = getUserRole(userDetails?.UserRole, orgData?.TenantType); // Set User Role Enum
          setUserRole(user);
          setOrgDetail(orgData);
          setUserDetail(userDetails);

          /**Code to set devices data at global level**/

          setIdelTimer(userDetails?.KeepMeLoggedInDays);
          updateLogin(
            devicekey,
            username,
            lastLoginTime,
            userDetails?.KeepMeLoggedInDays
          );
        })
        .catch((err) => {});
    },
    [
      attachIoTPolicy,
      setIdelTimer,
      addReportsSub,
      eventSubscribe,
      graphSubscription,
      summarySubscription,
    ]
  );

  const checkUser = useCallback(async () => {
    try {
      const data = await Auth.currentAuthenticatedUser();
      const username = data?.signInUserSession?.accessToken?.payload?.username;
      const devicekey =
        data?.signInUserSession?.accessToken?.payload?.device_key;
      setUserCognitoDetail(data);

      setUserDataForAPI(data); // For AxiosInterceptor

      const keepmelogin = localStorage.getItem("keepmelogin");
      const skeepmelogin = sessionStorage.getItem("keepmelogin");
      if (skeepmelogin == null) {
        if (keepmelogin == null || keepmelogin === "false") {
          await Auth.signOut();
          navigate("/signin");
        } else {
          sessionStorage.setItem("keepmelogin", "signin");
        }
      }
      getDetailsForCurrentUser(username, devicekey, data);
    } catch (err) {
      console.log(err);
      navigate("/signin");
    }
    //eslint-disable-next-line
  }, [getDetailsForCurrentUser]); // Don't add navigate in dependency array

  useEffect(() => {
    checkUser();
    return () => {
      if (reportSub.current) {
        reportSub.current.unsubscribe();
      }
      if (eventsSub.current) {
        eventsSub.current.unsubscribe();
      }
    };
  }, [checkUser]);

  useEffectOnce(() => {
    return () => {
      navigate("/signin");
    };
  });

  /**Context Value Starts */
  const userContext = useMemo(() => {
    return {
      userData: userDetail,
      setUserGlobalData: setUserDetail,
      userGlobalData: userDetail,
      userCognitoData: userCognitoDetail,
      loggedInUserRole: userRole,
      featureEnabledStatus: backendConfig.featureFlag,
      setNewTheme: setAppTheme,
      appTheme: appTheme,
      isDashboardSorting: isDashboardSorting,
      refreshRateDeviceView: refreshRateDeviceView,
      refreshRateDashboard: refreshRateDashboard,
      setRefreshRateDashboard: setRefreshRateDashboard,
      setRefreshRateDeviceView: setRefreshRateDeviceView,
    };
  }, [
    userDetail,
    userCognitoDetail,
    userRole,
    appTheme,
    setAppTheme,
    isDashboardSorting,
    setRefreshRateDashboard,
    setRefreshRateDeviceView,
    refreshRateDashboard,
    refreshRateDeviceView,
  ]);

  const orgContext = useMemo(() => {
    return { orgData: orgDetail, siteData: siteDetail, setSiteData: () => {} };
  }, [orgDetail, siteDetail]);

  const reportContext = useMemo(() => {
    return {
      reportUpdateStatus: reportDownloadDetail,
      setReportUpdateStatus: setReportDownloadDetail,
      graphReportStatus: graphReportStatus,
      setGraphReportStatus: setGraphReportStatus,
      summaryReportStatus: summaryReportStatus,
      setSummaryReportStatus: setSummaryReportStatus,
    };
  }, [reportDownloadDetail, graphReportStatus, summaryReportStatus]);

  const notificationContext = useMemo(() => {
    return {
      isNewMode: isNewMode,
      setIsNewMode: setIsNewMode,
      isNewNotification: isNewNotification,
      isEvtListedFromHeader: isEvtListedFromHeader,
      isEvtListedFromDevice: isEvtListedFromDevice,
      setIsEvtListedFromHeader: setIsEvtListedFromHeader,
      setIsEvtListedFromDevice: setIsEvtListedFromDevice,
    };
  }, [
    isNewMode,
    setIsNewMode,
    isNewNotification,
    isEvtListedFromDevice,
    isEvtListedFromHeader,
  ]);

  const localValuesContext = useMemo(() => {
    return {
      activeFilter: activeFilter,
      setActiveFilter: setActiveFilter,
      isShowUnread: isShowUnread,
      setIsShowUnread: setIsShowUnread,
      isShowEvent: isShowEvent,
      setIsShowEvent: setIsShowEvent
    };
  }, [
    activeFilter,
    setActiveFilter,
    isShowUnread,
    setIsShowUnread,
    isShowEvent,
    setIsShowEvent
  ]);
  /**Context Value Ends */

  return (
    <OrganisationContext.Provider value={orgContext}>
      <UserContext.Provider value={userContext}>
        <ReportsDownloadUpdate.Provider value={reportContext}>
          <NotificationContext.Provider value={notificationContext}>
            <LocalValuesContext.Provider value={localValuesContext}>
            <div
              className={`dashboardWrapper ${
                appTheme === "dark" ? "dark-theme" : "light-theme"
              }`}
            >
              <div className="leftPanel">
                {loadMainData ? <Sidebar /> : <></>}
              </div>
              <div className="rightPanel relative">
                <PagesHeader updateSiteDetails={setSiteDetail} />
                <div className="bodyWrapper">
                  {loadMainData ? (
                    <AppRoutes
                      globalDeviceDetails={globalDeviceDetails}
                      globalLocationDetails={globalLocationDetails}
                      deviceQueryCount={deviceQueryCount}
                    />
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            </div>
            {isLoading && <LoadingSpinner />}
          </LocalValuesContext.Provider>
          </NotificationContext.Provider>
        </ReportsDownloadUpdate.Provider>
      </UserContext.Provider>
    </OrganisationContext.Provider>
  );
}

export default MainLayout;
